import type { ProgrammaticLandingPageConfig } from '../../types'
import {
  STORAGE_REFERENCE_KWP,
  SELF_CONSUMPTION_WITHOUT_BATTERY,
  SELF_CONSUMPTION_BATTERY_BONUS,
  SELF_CONSUMPTION_WITH_BATTERY,
  BATTERY_COST_PER_KWH_RANGE_EUR,
} from '../storageSizeFacts'

export const KWH_15_MAGAZYN_ENERGII: ProgrammaticLandingPageConfig = {
  slug: 'magazyn-energii-15-kwh-cena',
  lang: 'pl',
  topicId: 'kwh-speicher-15',
  title: 'Magazyn energii 15 kWh: cena i wartości orientacyjne 2026',
  metaDescription:
    'Ile kosztuje magazyn energii 15 kWh? Dla kogo się nadaje, wartości orientacyjne ' +
    'autokonsumpcji i bezpłatne porównanie ofert na instalację + magazyn.',
  keywords: [
    'magazyn energii 15 kWh cena',
    'magazyn energii 15 kWh koszt',
    'bateria fotowoltaiczna 15 kWh',
    'ile kosztuje magazyn energii 15 kWh',
  ],
  eyebrow: 'Wielkość magazynu: 15 kWh',
  h1: 'Magazyn energii 15 kWh: cena i wartości orientacyjne',
  intro:
    'Magazyn energii 15 kWh opłaca się przede wszystkim dla gospodarstw z pompą ciepła lub ' +
    `regularnym ładowaniem samochodu elektrycznego, w połączeniu z referencyjną instalacją ` +
    `${STORAGE_REFERENCE_KWP} kWp. Znajdziesz tu wartości orientacyjne dotyczące odpowiedniej ` +
    'wielkości gospodarstwa, autokonsumpcji z magazynem energii, a tuż poniżej kalkulator z już ' +
    'wypełnioną średnią ceną dla tego połączenia, obliczoną na podstawie prawdziwych ofert firm ' +
    'rzemieślniczych.',
  calculatorPrefill: { kind: 'kwp+storage', kwp: STORAGE_REFERENCE_KWP, kwh: 15 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Dla kogo odpowiedni jest magazyn energii 15 kWh?' },
    {
      type: 'paragraph',
      text:
        'Wartość orientacyjna: 15 kWh pasuje do gospodarstw z pompą ciepła lub regularnym ' +
        'ładowaniem samochodu elektrycznego. Przy obu dużych odbiornikach jednocześnie może ' +
        'opłacać się nawet wielkość 20 kWh.',
    },
    { type: 'heading', text: 'Autokonsumpcja z magazynem energii' },
    {
      type: 'paragraph',
      text:
        `Bez magazynu udział autokonsumpcji w instalacji ${STORAGE_REFERENCE_KWP} kWp wynosi ` +
        `zwykle ok. ${SELF_CONSUMPTION_WITHOUT_BATTERY * 100} %. Z magazynem energii udział ten ` +
        `rośnie w modelu obliczeniowym o ${SELF_CONSUMPTION_BATTERY_BONUS * 100} punktów ` +
        `procentowych, do ok. ${SELF_CONSUMPTION_WITH_BATTERY * 100} % - pompa ciepła i samochód ` +
        'elektryczny wnoszą dodatkowo każde swój własny bonus w modelu, aż do granicy 85 %. ' +
        'Dokładne obliczenie dla Twojego zużycia daje kalkulator doboru instalacji.',
    },
    { type: 'heading', text: 'Zasilanie awaryjne' },
    {
      type: 'paragraph',
      text:
        'Przy 15 kWh skrzynka backup do zasilania awaryjnego zwykle wystarcza również na większe ' +
        'części domu przez dłuższy czas - dokładny czas zależy od rzeczywistego zużycia ' +
        'podłączonych urządzeń. Czy i w jakim zakresie dana oferta obejmuje zasilanie awaryjne, ' +
        'zależy od konkretnej firmy - warto o to zapytać bezpośrednio.',
    },
    { type: 'heading', text: 'Koszty magazynu i dofinansowanie' },
    {
      type: 'paragraph',
      text:
        'Koszty magazynów energii mocno się różnią w zależności od producenta i złożoności ' +
        'montażu - badanie rynku z 2026 roku pokazuje przedział od ok. ' +
        `${BATTERY_COST_PER_KWH_RANGE_EUR.min} do ${BATTERY_COST_PER_KWH_RANGE_EUR.max.toLocaleString('pl-PL')} ` +
        '€ za kWh, co przy 15 kWh wyraźnie odczuwalne jest w całkowitym budżecie. W Niemczech ' +
        'istnieją regionalne dofinansowania specjalnie na magazyny energii (np. program kraju ' +
        'związkowego Berlin z dopłatą do magazynów energii); w Austrii system EAG dolicza ' +
        '150 €/kWh za magazyn - dokładne obliczenie dla Twojego połączenia daje kalkulator poniżej.',
    },
  ],
  faq: [
    {
      q: 'Czy potrzebuję 15 kWh, jeśli mam pompę ciepła?',
      a: 'Pompa ciepła zwiększa możliwy udział autokonsumpcji w modelu obliczeniowym - 15 kWh to popularna wielkość dla takiego połączenia. Dokładna wielkość zależy od rzeczywistego zużycia na ogrzewanie, zobacz kalkulator doboru instalacji.',
    },
    {
      q: 'Czy 15 kWh to za dużo dla gospodarstw bez pompy ciepła i samochodu elektrycznego?',
      a: 'W wielu przypadkach tak - bez tych dużych odbiorników 10 lub 12 kWh zwykle wystarcza, by pokryć większość możliwej autokonsumpcji.',
    },
    {
      q: 'Z jaką wielkością instalacji łączony jest zwykle magazyn 15 kWh?',
      a: `Na tej stronie jako punkt odniesienia przyjęto instalację ${STORAGE_REFERENCE_KWP} kWp, w praktyce często łączony też z 15 kWp - dokładną rekomendację dla Twojego dachu daje kalkulator doboru instalacji.`,
    },
    {
      q: 'Czy 15 kWh wystarczy na pełną samowystarczalność w nocy?',
      a: 'Tak, to zwykle wystarcza nawet przy zużyciu powyżej średniej, np. z ładowaniem samochodu elektrycznego lub pompą ciepła. Dokładną ocenę dla Twojego rzeczywistego zużycia daje kalkulator doboru instalacji.',
    },
  ],
  primaryCta: { label: 'Poproś teraz o bezpłatną ofertę', action: 'customer' },
  secondaryCta: {
    label: 'Koszt instalacji fotowoltaicznej: pełny przewodnik',
    action: 'link',
    href: '/koszt-instalacji-fotowoltaicznej',
  },
  relatedLinks: [
    { label: 'Magazyn energii 12 kWh: cena i wartości orientacyjne', href: '/magazyn-energii-12-kwh-cena' },
    { label: 'Magazyn energii 20 kWh: cena i wartości orientacyjne', href: '/magazyn-energii-20-kwh-cena' },
    { label: 'Instalacja fotowoltaiczna 15 kWp: cena i wartości orientacyjne', href: '/instalacja-fotowoltaiczna-15-kwp-cena' },
    { label: 'Jak działa porównanie ofert', href: '/porownaj-oferty-fotowoltaiczne' },
  ],
}
